import * as sitemapTypes from '~~/common/config/SitemapType';
import type { SitemapType } from '~~/common/config/SitemapType';

export const PROCESS_MESSAGE_NOTIFY_REQUEST = 'notifyRequest';
export const PROCESS_MESSAGE_GET_SITEMAP = 'getSitemap';
export const PROCESS_MESSAGE_NOTIFY_MEMORY = 'notifyMemory';

export type MessageKey = (
  | typeof PROCESS_MESSAGE_NOTIFY_REQUEST
  | typeof PROCESS_MESSAGE_GET_SITEMAP
  | typeof PROCESS_MESSAGE_NOTIFY_MEMORY
);

// Type map that associates each message key with its specific payload structure
export type ProcessMessagePayloadMap = {
  [PROCESS_MESSAGE_NOTIFY_REQUEST]: Record<string, never>; // no payload
  [PROCESS_MESSAGE_GET_SITEMAP]: {
    sitemapType: SitemapType;
    data: { loc: string }[] | null;
  };
  [PROCESS_MESSAGE_NOTIFY_MEMORY]: {
    workerIndex: number;
    metrics: Array<{
      timestamp: number; // Unix timestamp in seconds (when collected)
      heapUsed: number; // in MB
      rss: number; // in MB
      heapTotal: number; // in MB
      external: number; // in MB
    }>;
  };
};

// Full typed message with cmd, pid, and payload
export type ProcessMessage<T extends MessageKey> = {
  cmd: T;
  pid: number;
} & ProcessMessagePayloadMap[T];

// Runtime validators for each message type's payload
export const messagePayloadValidators: {
  [K in MessageKey]: (payload: unknown) => payload is ProcessMessagePayloadMap[K]
} = {
  [PROCESS_MESSAGE_NOTIFY_REQUEST]: (payload): payload is ProcessMessagePayloadMap[typeof PROCESS_MESSAGE_NOTIFY_REQUEST] => {
    return typeof payload === 'object' && payload !== null;
  },
  [PROCESS_MESSAGE_GET_SITEMAP]: (payload): payload is ProcessMessagePayloadMap[typeof PROCESS_MESSAGE_GET_SITEMAP] => {
    if (typeof payload !== 'object' || payload === null) {
      return false;
    }
    const { sitemapType } = payload as Record<string, unknown>;
    const isValidSitemapType = typeof sitemapType === 'string' && Object.values(sitemapTypes).some(st => st === sitemapType);
    return isValidSitemapType;
  },
  [PROCESS_MESSAGE_NOTIFY_MEMORY]: (payload): payload is ProcessMessagePayloadMap[typeof PROCESS_MESSAGE_NOTIFY_MEMORY] => {
    if (typeof payload !== 'object' || payload === null) {
      return false;
    }
    const { workerIndex, metrics } = payload as Record<string, unknown>;
    if (typeof workerIndex !== 'number' || workerIndex < 0) {
      return false;
    }
    if (!Array.isArray(metrics) || metrics.length === 0) {
      return false;
    }
    // Validate each metric in the array
    return metrics.every((metric) => {
      if (typeof metric !== 'object' || metric === null) {
        return false;
      }
      const { timestamp, heapUsed, rss, heapTotal, external } = metric as Record<string, unknown>;
      return (
        typeof timestamp === 'number' &&
        typeof heapUsed === 'number' &&
        typeof rss === 'number' &&
        typeof heapTotal === 'number' &&
        typeof external === 'number'
      );
    });
  },
};
